import { useQuery } from '@tanstack/react-query';
import { useState, useTransition, useDeferredValue } from 'react';
import useDebounce from './useDebounce';

interface UseCustomAutocompleteProps {
  defaultData?: Record<string, any>;
  fetchOptions: (queryValue?: string, defaultData?: Record<string, any>) => Promise<any>;
  queryKey: string;
  isDropdownMode?: boolean;
  isFetchingDisabled?: boolean
}

export const useCustomAutocomplete = ({
  queryKey,
  defaultData = {},
  fetchOptions,
  isDropdownMode = false,
  isFetchingDisabled = false
}: UseCustomAutocompleteProps) => {
  const [inputValue, setInputValue] = useState('');
  const [queryValue, setQueryValue] = useState('');
  const [isPending, startTransition] = useTransition();
  const [open, setOpen] = useState(false);

  const debouncedFunction = useDebounce(debouncedSetQueryValue, 500);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [queryKey, queryValue],
    queryFn: () => (isDropdownMode ? fetchOptions() : fetchOptions(queryValue, defaultData)),
    enabled: open && !isFetchingDisabled && !!fetchOptions,
    staleTime: 10 * 60 * 1000,
  });  
  const deferredOptions = useDeferredValue(data || []);

  function debouncedSetQueryValue(value: string) {
    startTransition(() => {
      setQueryValue(value);
    });
  }

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (!isDropdownMode) {
      debouncedFunction(value);
    }
  };

  const onOpen = () => {
    setOpen(true);
    if (isDropdownMode) {
      refetch();
    }
  };

  const onClose = () => {
    setOpen(false);
    if (!isDropdownMode) {
      handleInputChange('');
    }
  };

  return {
    options: deferredOptions,
    isLoading,
    isPending,
    isError,
    inputValue,
    handleInputChange,
    refetch,
    onOpen,
    onClose,
  };
};
